import React, { useEffect, useRef, useState } from "react";
import { useLaneCopy } from "../../utils/laneCopy";
import { useIndonesiaV2Language } from "./indonesiaV2/indonesiaV2Language";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";

// ─── Types ────────────────────────────────────────────────────────────────────

interface PersonData {
  dob: string;
  role: string;
}

const emptyPerson = (): PersonData => ({ dob: "", role: "" });

// ─── Bilingual copy (English / Bahasa Indonesia) ──────────────────────────────

const COPY = {
  en: {
    dateOfBirth: "Date of Birth",
    dobPlaceholder: "DD / MM / YYYY",
    relationshipRole: "Relationship / Role",
    loadingSteps: [
      { label: "Reading the energy between you two...", icon: "✧" },
      { label: "Sensing where things feel out of sync...", icon: "◈" },
      { label: "Finding what would help right now...", icon: "◉" },
      { label: "Shaping today's advice...", icon: "☉" },
      { label: "Writing your reading...", icon: "🕊" },
    ],
    readingTitle: "Reading Your Energy Match",
    readingTag: "Energy Match V3",
    engineTag: "Relationship Energy Engine",
    pageTitle: "Energy Match V3",
    pageSubtitle: "How your energy and theirs fit together",
    youTitle: "You",
    otherTitle: "The Other Person",
    rolePlaceholderYou: "e.g. Partner, Parent, Friend",
    rolePlaceholderOther: "e.g. Boyfriend, Daughter, Coworker",
    currentSituation: "Current Situation",
    currentSituationPlaceholder: "What's going on between you two right now?",
    yourGoal: "Your Goal",
    yourGoalPlaceholder: "What do you want to happen?",
    submit: "💞 Run Energy Match",
    footerNote: "a gentle read on your connection — not a verdict on anyone",
    errYourDob: "Your date of birth is required.",
    errOtherDob: "The other person's date of birth is required.",
    errRunFailed: "Failed to run scan. Please try again.",
    errGeneric: "Something went wrong. Please try again.",
  },
  id: {
    dateOfBirth: "Tanggal Lahir",
    dobPlaceholder: "TGL / BLN / THN",
    relationshipRole: "Hubungan / Peran",
    loadingSteps: [
      { label: "Membaca energi di antara kalian berdua...", icon: "✧" },
      { label: "Merasakan bagian yang kurang selaras...", icon: "◈" },
      { label: "Mencari hal yang bisa membantu saat ini...", icon: "◉" },
      { label: "Menyusun saran untuk hari ini...", icon: "☉" },
      { label: "Menulis hasil bacaan Anda...", icon: "🕊" },
    ],
    readingTitle: "Membaca Energy Match Anda",
    readingTag: "Energy Match V3",
    engineTag: "Mesin Energi Hubungan",
    pageTitle: "Energy Match V3",
    pageSubtitle: "Bagaimana energi Anda dan mereka saling cocok",
    youTitle: "Anda",
    otherTitle: "Orang Lain",
    rolePlaceholderYou: "cth. Pasangan, Orang Tua, Teman",
    rolePlaceholderOther: "cth. Pacar, Anak, Rekan Kerja",
    currentSituation: "Situasi Saat Ini",
    currentSituationPlaceholder:
      "Apa yang sedang terjadi di antara kalian berdua saat ini?",
    yourGoal: "Tujuan Anda",
    yourGoalPlaceholder: "Apa yang Anda inginkan terjadi?",
    submit: "💞 Jalankan Energy Match",
    footerNote:
      "bacaan lembut tentang hubungan Anda — bukan vonis untuk siapa pun",
    errYourDob: "Tanggal lahir Anda wajib diisi.",
    errOtherDob: "Tanggal lahir orang lain wajib diisi.",
    errRunFailed: "Gagal menjalankan pemindaian. Silakan coba lagi.",
    errGeneric: "Terjadi kesalahan. Silakan coba lagi.",
  },
} as const;

type Lang = keyof typeof COPY;
type Copy = (typeof COPY)[Lang];

// Converts a displayed DD/MM/YYYY value to the ISO YYYY-MM-DD format the backend validator requires.
const dobToIso = (value: string): string => {
  const parts = value.split("/");
  if (parts.length !== 3) return value;
  const [day, month, year] = parts;
  if (!day || !month || !year) return value;
  return `${year.padStart(4, "0")}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
};

// ─── Date of Birth formatting helpers (mirrors EnergyMatchDesign styling) ─────

const formatDateValue = (value: string): string => {
  const digits = value.replace(/\D/g, "");
  const limitedDigits = digits.slice(0, 8);
  let formatted = "";
  for (let i = 0; i < limitedDigits.length; i++) {
    if (i === 2 || i === 4) {
      formatted += "/";
    }
    formatted += limitedDigits[i];
  }
  return formatted;
};

const validateDateValue = (value: string): string => {
  if (!value) return value;
  const parts = value.split("/");
  if (parts.length === 3) {
    let day = parseInt(parts[0], 10);
    let month = parseInt(parts[1], 10);
    let year = parseInt(parts[2], 10);
    if (isNaN(day)) day = 1;
    if (day < 1) day = 1;
    if (day > 31) day = 31;
    if (isNaN(month)) month = 1;
    if (month < 1) month = 1;
    if (month > 12) month = 12;
    if (isNaN(year)) year = new Date().getFullYear();
    if (year < 1900) year = 1900;
    if (year > 2099) year = 2099;
    return `${day.toString().padStart(2, "0")}/${month.toString().padStart(2, "0")}/${year}`;
  }
  return value;
};

const DateOfBirthField: React.FC<{
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  t: Copy;
}> = ({ value, onChange, required, t }) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const rawValue = input.value;
    const cursorPosition = input.selectionStart || 0;
    const oldFormatted = rawValue;
    const newFormatted = formatDateValue(rawValue);
    onChange(newFormatted);
    setTimeout(() => {
      if (input === document.activeElement) {
        let newCursorPos = cursorPosition;
        if (cursorPosition === 3 && newFormatted.length > oldFormatted.length) {
          newCursorPos = 4;
        } else if (
          cursorPosition === 6 &&
          newFormatted.length > oldFormatted.length
        ) {
          newCursorPos = 7;
        }
        input.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  };

  return (
    <div>
      <label className="block text-[10px] sm:text-[11px] font-semibold tracking-[0.18em] text-[#f7f3fa] uppercase">
        {t.dateOfBirth}
        {required && <span className="ml-1 text-[#C084FC]">*</span>}
      </label>
      <input
        type="text"
        inputMode="numeric"
        placeholder={t.dobPlaceholder}
        value={value}
        onChange={handleChange}
        onBlur={(e) => onChange(validateDateValue(e.target.value))}
        className="mt-1.5 w-full rounded-full border border-white/10 bg-[#1a131f] px-4 sm:px-5 py-2.5 sm:py-3 text-sm sm:text-[15px] text-[#f7f3fa] shadow-sm focus:border-[#C084FC] focus:outline-none focus:ring-1 focus:ring-[#C084FC]"
      />
    </div>
  );
};

// ─── Shared UI atoms (mirrors MatescanForm/CheckMateForm styling) ─────────────

const InputField: React.FC<{
  label: string;
  type?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  required?: boolean;
}> = ({ label, type = "text", value, onChange, placeholder, required }) => (
  <div>
    <label
      className="block text-[10px] font-semibold tracking-[0.15em] uppercase mb-1.5"
      style={{ color: "#c8bfd1" }}
    >
      {label}
      {required && (
        <span className="ml-1" style={{ color: "#C084FC" }}>
          *
        </span>
      )}
    </label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full rounded-xl px-4 py-2.5 text-sm transition-all focus:outline-none"
      style={{
        background: "#1a131f",
        border: "1px solid rgba(255,255,255,0.12)",
        color: "#f7f3fa",
      }}
      onFocus={(e) => {
        e.currentTarget.style.borderColor = "#C084FC";
        e.currentTarget.style.boxShadow = "0 0 0 2px #C084FC18";
      }}
      onBlur={(e) => {
        e.currentTarget.style.borderColor = "rgba(255,255,255,0.12)";
        e.currentTarget.style.boxShadow = "none";
      }}
    />
  </div>
);

const PersonBlock: React.FC<{
  title: string;
  badge: string;
  rolePlaceholder: string;
  data: PersonData;
  onChange: (field: keyof PersonData, value: string) => void;
  t: Copy;
}> = ({ title, badge, rolePlaceholder, data, onChange, t }) => (
  <div>
    <div className="flex items-center gap-2 mb-4">
      <span
        className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0"
        style={{ background: "linear-gradient(135deg,#C084FC,#6D4C92)" }}
      >
        {badge}
      </span>
      <h3
        className="text-xs font-semibold tracking-[0.15em] uppercase"
        style={{ color: "#c8bfd1" }}
      >
        {title}
      </h3>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <DateOfBirthField
        value={data.dob}
        onChange={(v) => onChange("dob", v)}
        required
        t={t}
      />
      <InputField
        label={t.relationshipRole}
        value={data.role}
        onChange={(v) => onChange("role", v)}
        placeholder={rolePlaceholder}
      />
    </div>
  </div>
);

// ─── Loading Screen (mirrors MatescanForm's LoadingScreen) ────────────────────

const LoadingScreen: React.FC<{ t: Copy }> = ({ t }) => {
  const [activeIdx, setActiveIdx] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const steps = t.loadingSteps;

  useEffect(() => {
    timerRef.current = setInterval(() => {
      setActiveIdx((prev) => {
        const next = prev + 1;
        if (next >= steps.length) {
          clearInterval(timerRef.current!);
          return prev;
        }
        return next;
      });
    }, 420);
    return () => clearInterval(timerRef.current!);
  }, [steps.length]);

  const currentStep = steps[activeIdx];

  return (
    <div className="min-h-screen w-full bg-[#0f0a14] [color-scheme:dark] flex flex-col items-center justify-center px-6 py-16 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute -top-24 -left-24 w-72 h-72 rounded-full blur-3xl"
          style={{
            background: "radial-gradient(circle, #C084FC 0%, transparent 70%)",
            opacity: 0.07,
          }}
        />
        <div
          className="absolute -bottom-24 -right-24 w-80 h-80 rounded-full blur-3xl"
          style={{
            background: "radial-gradient(circle, #C084FC 0%, transparent 70%)",
            opacity: 0.1,
          }}
        />
      </div>

      <div className="relative mb-10">
        <svg
          width="112"
          height="112"
          viewBox="0 0 112 112"
          className="animate-spin"
          style={{ animationDuration: "6s" }}
        >
          <circle
            cx="56"
            cy="56"
            r="48"
            fill="none"
            stroke="#C084FC"
            strokeWidth="1"
            strokeDasharray="5 7"
            opacity="0.35"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#C084FC]/20 to-[#6D4C92]/20 border border-[#C084FC]/30 flex items-center justify-center">
            <span className="text-xl text-[#d9ccff]">💞</span>
          </div>
        </div>
      </div>

      <motion.h2
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-xl sm:text-2xl font-light tracking-wide mb-1 text-center"
        style={{ color: "#c8bfd1" }}
      >
        {t.readingTitle}
      </motion.h2>
      <p
        className="text-xs tracking-[0.18em] uppercase mb-10 text-center"
        style={{ color: "#a89bb5" }}
      >
        {t.readingTag}
      </p>

      <div className="w-full max-w-xs min-h-[52px] flex items-center justify-center">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeIdx}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
            className="flex items-center gap-3 px-5 py-3 rounded-2xl bg-[#221a29] border border-[#C084FC]/30 shadow-sm"
          >
            <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-gradient-to-br from-[#C084FC] to-[#6D4C92]">
              <span className="text-white text-sm">{currentStep.icon}</span>
            </div>
            <span className="text-sm font-medium" style={{ color: "#c8bfd1" }}>
              {currentStep.label}
            </span>
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="flex gap-2 mt-6">
        {steps.map((_, i) => (
          <div
            key={i}
            className="rounded-full transition-all duration-500"
            style={{
              width: i === activeIdx ? 20 : 6,
              height: 6,
              background: i <= activeIdx ? "#C084FC" : "rgba(255,255,255,0.12)",
            }}
          />
        ))}
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const EnergyMatchV3Form: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const categoryId = searchParams.get("categoryId") || undefined;
  const { user: authUser } = useAuth();
  const lang: Lang = useIndonesiaV2Language();
  const t = useLaneCopy("em3form", COPY);

  const [user, setUser] = useState<PersonData>(emptyPerson());
  const [partner, setPartner] = useState<PersonData>(emptyPerson());
  const [context, setContext] = useState("");
  const [goal, setGoal] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateUser = (field: keyof PersonData, value: string) => {
    setUser((prev) => ({ ...prev, [field]: value }));
  };

  const updatePartner = (field: keyof PersonData, value: string) => {
    setPartner((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!user.dob) {
      setError(t.errYourDob);
      return;
    }
    if (!partner.dob) {
      setError(t.errOtherDob);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const payload = {
        user: { ...user, dob: dobToIso(user.dob) },
        partner: { ...partner, dob: dobToIso(partner.dob) },
        context,
        goal,
        userId: localStorage.getItem("userId") || undefined,
        categoryId,
      };
      const response = await api.post("/backend/energy-match-v2/scan", payload);
      if (response.data?.success) {
        sessionStorage.setItem(
          "energyMatchV3Scan",
          JSON.stringify(response.data),
        );
        navigate("/energyMatchV3/result");
      } else {
        setLoading(false);
        const errs: string[] = response.data?.errors;
        setError(
          Array.isArray(errs) && errs.length > 0
            ? errs.join(" · ")
            : response.data?.message || t.errRunFailed,
        );
      }
    } catch (err: any) {
      setLoading(false);
      const errs: string[] = err?.response?.data?.errors;
      setError(
        Array.isArray(errs) && errs.length > 0
          ? errs.join(" · ")
          : err?.response?.data?.message || t.errGeneric,
      );
    }
  };

  if (loading) return <LoadingScreen t={t} />;

  return (
    <div className="min-h-screen w-full bg-[#0f0a14] [color-scheme:dark]">
      <div className="h-1 w-full" />

      <div className="min-h-screen w-full px-4 sm:px-6 md:px-10 lg:px-16 py-8 sm:py-12">
        <div className="w-full max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-6 sm:mb-8">
            <div className="inline-flex items-center gap-2 mb-3">
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{ background: "#C084FC" }}
              />
              <span
                className="text-[10px] tracking-[0.25em] uppercase"
                style={{ color: "#a89bb5" }}
              >
                {t.engineTag}
              </span>
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{ background: "#C084FC" }}
              />
            </div>
            <h1
              className="text-xl sm:text-2xl md:text-3xl font-light tracking-wide"
              style={{ color: "#c8bfd1" }}
            >
              {t.pageTitle}
            </h1>
            <p className="text-xs sm:text-sm mt-2" style={{ color: "#a89bb5" }}>
              {t.pageSubtitle}
            </p>
          </div>

          {/* Card */}
          <div
            className="rounded-3xl p-5 sm:p-6 md:p-8 shadow-sm border"
            style={{ background: "#1a131f", borderColor: "rgba(255,255,255,0.1)" }}
          >
            <div className="space-y-6 sm:space-y-8">
              <PersonBlock
                title={t.youTitle}
                badge="★"
                rolePlaceholder={t.rolePlaceholderYou}
                data={user}
                onChange={updateUser}
                t={t}
              />

              <div
                className="border-t pt-6 sm:pt-8"
                style={{ borderColor: "rgba(255,255,255,0.1)" }}
              >
                <PersonBlock
                  title={t.otherTitle}
                  badge="2"
                  rolePlaceholder={t.rolePlaceholderOther}
                  data={partner}
                  onChange={updatePartner}
                  t={t}
                />
              </div>

              <div
                className="border-t pt-6 sm:pt-8 grid grid-cols-1 gap-4"
                style={{ borderColor: "rgba(255,255,255,0.1)" }}
              >
                <InputField
                  label={t.currentSituation}
                  value={context}
                  onChange={setContext}
                  placeholder={t.currentSituationPlaceholder}
                />
                <InputField
                  label={t.yourGoal}
                  value={goal}
                  onChange={setGoal}
                  placeholder={t.yourGoalPlaceholder}
                />
              </div>
            </div>
          </div>

          {/* Error */}
          <AnimatePresence>
            {error && (
              <motion.p
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="mt-3 text-red-500 text-xs text-center font-medium"
              >
                {error}
              </motion.p>
            )}
          </AnimatePresence>

          {/* Submit */}
          <div className="mt-6">
            <button
              onClick={handleSubmit}
              className="w-full rounded-full py-3 px-8 text-sm font-semibold text-white shadow-lg transition-all duration-200 hover:opacity-90"
              style={{
                background: "#a78bff", color: "#0f0a14", boxShadow: "0 0 22px rgba(167,139,255,0.35)", borderRadius: 14,
              }}
            >
              {t.submit}
            </button>
          </div>

          <div
            className="text-center mt-6 text-[10px] sm:text-[11px] tracking-wide"
            style={{ color: "#a89bb5" }}
          >
            <span
              className="inline-block border-t w-8 sm:w-12 mx-2 align-middle"
              style={{ borderColor: "#C084FC40" }}
            />
            {t.footerNote}
            <span
              className="inline-block border-t w-8 sm:w-12 mx-2 align-middle"
              style={{ borderColor: "#C084FC40" }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnergyMatchV3Form;
